import { useState } from "react";
import { ProductStatusBadge } from "@/components/status-badges";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { toast } from "sonner";
import { useProducts, useUpdateProduct, useDeleteProduct } from "@/pages/product/hooks/useProducts";
import type { Product } from "@/pages/product/hooks/useProducts";
import { ProductFormDialog } from "@/pages/product/components/ProductFormDialog";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { SkeletonTable } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";
import { RowActions, RowActionItem, RowActionSeparator } from "@/components/row-actions";
import { Search, PackagePlus, Package, Trash2, Archive, RotateCcw, Pencil } from "lucide-react";

export function Products() {
  const { data: products, isLoading } = useProducts();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [search, setSearch] = useState("");

  const filteredProducts = products?.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.size.toLowerCase().includes(q);
  });

  const archiveProduct = async (id: number) => {
    try {
      await updateProduct.mutateAsync({ id, data: { status: "archived" } });
      toast.success("Success", { description: "Product archived" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to archive product" });
    }
  };

  const activateProduct = async (id: number) => {
    try {
      await updateProduct.mutateAsync({ id, data: { status: "active" } });
      toast.success("Success", { description: "Product activated" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to activate product" });
    }
  };

  const removeProduct = async (id: number) => {
    try {
      await deleteProduct.mutateAsync(id);
      toast.success("Deleted", { description: "Product removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete product" });
    }
  };

  const actions = (
    <Button onClick={() => setCreateOpen(true)}>
      <PackagePlus className="size-4" />
      Add Product
    </Button>
  );

  const rowActions = (product: NonNullable<typeof filteredProducts>[number]) => (
    <RowActions>
      <RowActionItem onClick={() => setEditingProduct(product)}>
        <Pencil className="size-4" />
        Edit
      </RowActionItem>
      {product.status === "active" ? (
        <RowActionItem
          onClick={() => archiveProduct(product.id)}
          disabled={updateProduct.isPending}
        >
          <Archive className="size-4" />
          Archive
        </RowActionItem>
      ) : (
        <RowActionItem
          onClick={() => activateProduct(product.id)}
          disabled={updateProduct.isPending}
        >
          <RotateCcw className="size-4" />
          Activate
        </RowActionItem>
      )}
      <RowActionSeparator />
      <RowActionItem
        destructive
        onClick={() => removeProduct(product.id)}
        disabled={deleteProduct.isPending}
      >
        <Trash2 className="size-4" />
        Delete
      </RowActionItem>
    </RowActions>
  );

  return (
    <PageLayout title="Products" description="Manage your products" actions={actions}>
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or size..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading && (
          <div className="overflow-hidden rounded-lg border">
            <SkeletonTable columns={5} rows={6} />
          </div>
        )}

        {!isLoading && filteredProducts && filteredProducts.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <ul className="md:hidden divide-y divide-border">
              {filteredProducts.map((product) => (
                <li key={product.id} className="flex items-center gap-2 py-3 pl-4 pr-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {product.name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{product.size}</span>
                      <ProductStatusBadge status={product.status} />
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-medium text-foreground">
                    ₹{product.unitPrice}
                  </span>
                  {rowActions(product)}
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.id} className="hover:bg-muted/50 border-border">
                      <TableCell className="text-sm text-foreground font-medium">
                        {product.name}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {product.size}
                      </TableCell>
                      <TableCell className="text-sm text-foreground text-right font-medium">
                        ₹{product.unitPrice}
                      </TableCell>
                      <TableCell>
                        <ProductStatusBadge status={product.status} />
                      </TableCell>
                      <TableCell>{rowActions(product)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {!isLoading && filteredProducts?.length === 0 && (
          <EmptyState
            icon={Package}
            title={search ? "No products found" : "No products yet"}
            description={
              search
                ? "Try adjusting your search terms"
                : "Get started by adding your first product"
            }
            action={
              !search && (
                <Button onClick={() => setCreateOpen(true)}>
                  <PackagePlus className="size-4" />
                  Add Product
                </Button>
              )
            }
          />
        )}
      </div>

      <ProductFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ProductFormDialog
        open={Boolean(editingProduct)}
        onOpenChange={(open) => {
          if (!open) setEditingProduct(null);
        }}
        product={editingProduct}
      />
    </PageLayout>
  );
}
